/**
 * Machined-metal button finishes.
 *
 * Same four-part shadow stack and 50% waist on every face so the set reads as
 * parts milled from one billet. Change only the hue ramp between variants.
 *
 *   steel     — primary / the control that moves the convoy
 *   gun       — secondary / quiet workhorse
 *   titanium  — setup / continue
 *   anodized  — destructive
 *   hazard    — armed / Go live (once per screen)
 *   dlc       — icon tool-rail
 */
import type { CSSObject } from '@mui/material/styles';

export type MetalFinish = 'steel' | 'gun' | 'titanium' | 'anodized' | 'hazard' | 'dlc';

const TEAL = '#00b8a9';

const sheen = {
  content: '""',
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.28) 50%, transparent 62%)',
  transform: 'translateX(-120%)',
  transition: 'transform .55s ease',
  pointerEvents: 'none',
};

type Face = {
  color: string;
  background: string;
  boxShadow: string;
  textShadow: string;
  hoverFilter?: string;
  activeBackground?: string;
  activeBoxShadow: string;
};

const FACES: Record<MetalFinish, Face> = {
  steel: {
    color: '#181b1f',
    background: 'linear-gradient(180deg, #f4f6f8 0%, #d7dce2 45%, #c2c9d1 50%, #dde2e8 100%)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -1px 0 rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.6), 0 4px 10px rgba(0,0,0,0.35)',
    textShadow: '0 1px 0 rgba(255,255,255,0.5)',
    activeBackground: 'linear-gradient(180deg, #c9cfd7 0%, #dde2e8 100%)',
    activeBoxShadow: 'inset 0 2px 4px rgba(0,0,0,0.25), 0 1px 1px rgba(0,0,0,0.4)',
  },
  gun: {
    color: '#cdd4dc',
    background: 'linear-gradient(180deg, #3a4149 0%, #2a3038 48%, #232930 52%, #2e343c 100%)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.55), 0 3px 8px rgba(0,0,0,0.3)',
    textShadow: '0 -1px 0 rgba(0,0,0,0.5)',
    hoverFilter: 'brightness(1.12)',
    activeBackground: 'linear-gradient(180deg, #22272e 0%, #2e343c 100%)',
    activeBoxShadow: 'inset 0 2px 5px rgba(0,0,0,0.5)',
  },
  titanium: {
    color: '#e6e9ec',
    background:
      'repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 2px, rgba(0,0,0,0.05) 2px 3px, transparent 3px 4px), linear-gradient(180deg, #5c646d 0%, #454c55 50%, #3b424a 52%, #4c535c 100%)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -1px 0 rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.55), 0 4px 9px rgba(0,0,0,0.3)',
    textShadow: '0 -1px 0 rgba(0,0,0,0.45)',
    activeBoxShadow: 'inset 0 2px 5px rgba(0,0,0,0.45)',
  },
  anodized: {
    color: '#ffe2e0',
    background: 'linear-gradient(180deg, #a13835 0%, #7e2a28 48%, #6f2423 52%, #8a2f2d 100%)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.6), 0 3px 9px rgba(160,50,45,0.25)',
    textShadow: '0 -1px 0 rgba(0,0,0,0.45)',
    hoverFilter: 'brightness(1.1)',
    activeBoxShadow: 'inset 0 2px 5px rgba(0,0,0,0.5)',
  },
  hazard: {
    color: '#231a04',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0) 50%), linear-gradient(180deg, #f6c14b 0%, #daa021 52%, #c98f14 54%, #e5ad2c 100%)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.55), 0 4px 10px rgba(214,150,20,0.3)',
    textShadow: '0 1px 0 rgba(255,255,255,0.35)',
    activeBoxShadow: 'inset 0 2px 5px rgba(0,0,0,0.3)',
  },
  dlc: {
    color: '#9aa3ad',
    background:
      'repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0 2px, transparent 2px 4px), linear-gradient(180deg, #23272d 0%, #1a1e23 50%, #171b20 52%, #1e2228 100%)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.6)',
    textShadow: 'none',
    hoverFilter: 'brightness(1.15)',
    activeBoxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
};

function machine(finish: MetalFinish): CSSObject {
  const face = FACES[finish];
  return {
    position: 'relative',
    overflow: 'hidden',
    border: 'none',
    borderRadius: '9px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: face.color,
    background: face.background,
    backgroundColor: 'transparent',
    boxShadow: face.boxShadow,
    textShadow: face.textShadow,
    transition: 'transform .08s, filter .15s',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
      background: face.background,
      boxShadow: face.boxShadow,
      ...(face.hoverFilter ? { filter: face.hoverFilter } : {}),
      ...(finish === 'dlc' ? { color: '#e8ebee' } : {}),
    },
    '&:active': {
      transform: 'translateY(1px)',
      background: face.activeBackground || face.background,
      boxShadow: face.activeBoxShadow,
    },
    '&:focus-visible': {
      outline: `2px solid ${TEAL}`,
      outlineOffset: '3px',
    },
    '&::after': sheen,
    '&:hover::after': { transform: 'translateX(120%)' },
    '@media (prefers-reduced-motion: reduce)': {
      '&::after': { display: 'none' },
    },
    '&.Mui-disabled, &:disabled': {
      color: face.color,
      background: face.background,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      opacity: 0.45,
      filter: 'none',
      transform: 'none',
      '&::after': { display: 'none' },
    },
  };
}

export function metalSx(finish: MetalFinish): CSSObject {
  if (finish === 'dlc') {
    return {
      ...machine(finish),
      justifyContent: 'center',
    };
  }
  return machine(finish);
}

export const metalSteel = metalSx('steel');
export const metalGun = metalSx('gun');
export const metalTitanium = metalSx('titanium');
export const metalAnodized = metalSx('anodized');
export const metalHazard = metalSx('hazard');
export const metalDlc = metalSx('dlc');

/** Dropdown paper — same milled panel as the button faces sit on. */
export const metalMenuPaper: CSSObject = {
  marginTop: 8,
  minWidth: 180,
  borderRadius: '10px',
  backgroundImage: 'none',
  backgroundColor: '#191c21',
  background:
    'linear-gradient(180deg, #22272e 0%, #191c21 48%, #15181d 52%, #1b1f25 100%)',
  border: '1px solid #262b33',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.45), 0 8px 24px rgba(0,0,0,0.45)',
  overflow: 'hidden',
};

export const metalMenuItem: CSSObject = {
  gap: 1,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#cdd4dc',
  minHeight: 34,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255,255,255,0.07)',
    color: '#e8ebee',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.09)' },
  },
};
